import React, { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import VerticalTimeline, { TimelineStep } from "./VerticalTimeline";
import {
  VALIDATION_TIMELINE_STEPS,
  TERMINAL_TIMELINE_STEP_KEYS,
} from "../../constants/comparisonTimelineSteps.enum";
import {
  TimelineStatusResponse,
  ComparisonTimelineStep,
} from "../../types/timeline";
import { getTimelineStatus } from "../../services/timelineServices/comparisonTimelineService";
import {
  groupLogMessagesByEnumKey,
  resolveTimelineDisplayMessage,
  TimelinePlaceholderValues,
} from "../../utils/timelineMessage";
import { useToast, formattedJson } from "../../services/utils";
import {
  getResourceResultByResourceId,
  getValidationExecutionJobStatus,
  getValidationsParams,
} from "../../services/resourcesService";
import {
  getRuleExecutionJobStatus,
  getRuleResultByRuleId,
  getRuleParams,
} from "../../services/rulesService";

interface DynamicVerticalTimelineDialogProps {
  open: boolean;
  onClose: () => void;
  executionId: number | null;
  jobType?: string;
  title?: string;
  showActions?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  className?: string;
  timelineSteps?: ComparisonTimelineStep[];
  /** Fills step placeholders such as `{resource_id}` that the logs omit. */
  placeholderValues?: TimelinePlaceholderValues;
}

const DynamicVerticalTimelineDialog: React.FC<
  DynamicVerticalTimelineDialogProps
> = ({
  open,
  onClose,
  executionId,
  jobType = "validation",
  title,
  showActions = true,
  primaryButtonText = "Continue",
  secondaryButtonText = "Close",
  onPrimaryAction,
  onSecondaryAction,
  className = "",
  timelineSteps: timelineStepsProp = VALIDATION_TIMELINE_STEPS,
  placeholderValues,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef<boolean>(true);
  const isTerminalStepReachedRef = useRef<boolean>(false);
  // Get interval from environment variable, default to 60000ms (60 seconds)
  const intervalMs = parseInt(
    process.env.REACT_APP_TIMELINE_UPDATE_INTERVAL || "60000",
    10,
  );

  /**
   * Initialize timeline steps with all steps in "inactive" state
   * This prevents the blank flash when the dialog first opens
   */
  const getInitialTimelineSteps = (): TimelineStep[] => {
    return timelineStepsProp.map((step) => ({
      id: step.id.toString(),
      title: resolveTimelineDisplayMessage(step, undefined, placeholderValues),
      status: "inactive" as const,
    }));
  };

  const [timelineData, setTimelineData] = useState<TimelineStatusResponse[]>(
    [],
  );
  const [timelineSteps, setTimelineSteps] = useState<TimelineStep[]>(
    getInitialTimelineSteps(),
  );
  const [error, setError] = useState<string | null>(null);
  const [autoRefreshIntervalId, setAutoRefreshIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const [highestMatchedPosition, setHighestMatchedPosition] =
    useState<number>(0);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobStatusPollingIntervalId, setJobStatusPollingIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const [isLastStepCompleted, setIsLastStepCompleted] =
    useState<boolean>(false);
  const [isTerminalStepReached, setIsTerminalStepReached] =
    useState<boolean>(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<number>(0);
  const [validationParams, setValidationParams] = useState<any>(null);
  const [isParamsLoading, setIsParamsLoading] = useState<boolean>(false);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Terminal timeline step for the active job type
   * Comparison History and Validation History each end on their own cleanup step
   */
  const terminalStepEnumKey = TERMINAL_TIMELINE_STEP_KEYS[jobType] ?? null;

  /**
   * Store timeline data and detect terminal completion
   * Detection is based on the stable log_enum_key, not the display message text.
   * Once the terminal step is present, recurring auto-refresh is stopped for this
   * dialog session and is not scheduled again (DQ-8830)
   */
  const applyTimelineData = useCallback(
    (data: TimelineStatusResponse[]) => {
      if (!isMountedRef.current) return;
      setTimelineData(data);
      if (
        terminalStepEnumKey &&
        Array.isArray(data) &&
        data.some((item) => item.log_enum_key === terminalStepEnumKey)
      ) {
        isTerminalStepReachedRef.current = true;
        setIsTerminalStepReached(true);
      }
    },
    [terminalStepEnumKey],
  );

  // Reset terminal detection for each new dialog session
  React.useEffect(() => {
    isTerminalStepReachedRef.current = false;
    setIsTerminalStepReached(false);
  }, [open, executionId, jobType]);

  // Fetch timeline status data on initial load
  React.useEffect(() => {
    if (open && executionId && executionId > 0) {
      const fetchData = async () => {
        try {
          const data = await getTimelineStatus(
            executionId,
            jobType as "validation" | "comparison" | "generic_research_query",
          );
          applyTimelineData(data);
          if (isMountedRef.current) {
            setError(null);
          }
        } catch (err) {
          if (!isMountedRef.current) return;
          setError(
            err instanceof Error
              ? err.message
              : "Failed to fetch timeline data",
          );
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, executionId, jobType]);

  // Fetch params — extracted so it can be called from useEffect and from the refresh button
  const handleRefreshParams = React.useCallback(async () => {
    if (!executionId || executionId <= 0) return;
    setIsParamsLoading(true);
    try {
      let data: any = null;
      if (jobType === "validation") {
        data = await getValidationsParams({
          currentResourceResultId: executionId,
        });
      } else if (jobType === "comparison") {
        data = await getRuleParams({ currentRuleResultId: executionId });
      }
      setValidationParams(data || null);
    } catch (err) {
      console.error("Failed to fetch params:", err);
      setValidationParams(null);
    } finally {
      setIsParamsLoading(false);
    }
  }, [executionId, jobType]);

  // Trigger params fetch when params tab becomes active
  React.useEffect(() => {
    if (open && activeTab === 1) {
      handleRefreshParams();
    } else if (!open) {
      setValidationParams(null);
      setActiveTab(0);
    }
  }, [open, executionId, jobType, activeTab]);

  /**
   * Auto-refresh effect: Automatically refresh timeline data at regular intervals
   * - Starts when dialog opens
   * - Stops when dialog closes or component unmounts
   * - Stops once the terminal timeline step for the job type has been reached
   * - Interval is configurable via REACT_APP_TIMELINE_UPDATE_INTERVAL env variable
   */
  React.useEffect(() => {
    if (!open || !executionId || executionId <= 0 || isTerminalStepReached) {
      // Clear interval if dialog is closed
      if (autoRefreshIntervalId) {
        clearInterval(autoRefreshIntervalId);
        setAutoRefreshIntervalId(null);
      }
      return;
    }

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      handleRefresh();
    }, intervalMs);

    setAutoRefreshIntervalId(intervalId);

    // Cleanup: Clear interval when dialog closes or component unmounts
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, executionId, isTerminalStepReached]);

  /**
   * Memoized mapping function: Mark steps as active with conditional logic based on job_status
   *
   * When job_status === "completed":
   * - Find the highest matched step ID from API response
   * - Mark all steps from 1 to highest ID as "active" (cascading effect)
   * - Mark all steps after highest ID as "inactive"
   *
   * When job_status !== "completed":
   * - Only mark steps as "active" if their log_enum_key is found in API response (exact match only)
   * - If a step's log_enum_key is NOT in API response, mark it as "inactive"
   * - Do NOT automatically mark previous steps as active
   *
   * - Update highestMatchedPosition state (1-based render position) for auto-scroll
   * Prevents recreating the function on every render
   */
  const mapApiResponseToTimelineSteps = useCallback(
    (apiResponse: TimelineStatusResponse[]): TimelineStep[] => {
      // Create a set of active enum keys from API response (matching log_enum_key)
      const activeEnumKeys = new Set<string>();
      apiResponse.forEach((item) => {
        activeEnumKeys.add(item.log_enum_key);
      });
      // Logged messages carry the concrete values for steps templated per resource
      const logMessagesByEnumKey = groupLogMessagesByEnumKey(apiResponse);

      const mapSteps = (
        getStatus: (step: ComparisonTimelineStep) => "active" | "inactive",
      ): TimelineStep[] =>
        timelineStepsProp.map((step) => ({
          id: step.id.toString(),
          title: resolveTimelineDisplayMessage(
            step,
            logMessagesByEnumKey.get(step.log_enum_key),
            placeholderValues,
          ),
          status: getStatus(step),
        }));

      
       {
        // EXACT MATCHING LOGIC: When job is not completed, use exact matching only

        // Find the highest step ID that matches the API response for auto-scroll
        let highestId = 0;
        let highestPosition = 0;
        timelineStepsProp.forEach((step, index) => {
          if (activeEnumKeys.has(step.log_enum_key) && step.id > highestId) {
            highestId = step.id;
            highestPosition = index + 1;
          }
        });

        // Update the highest matched render position for auto-scroll
        setHighestMatchedPosition(highestPosition);

        // Map steps with exact matching logic
        // Only steps with matching enum keys are active
        // No cascading effect - each step is independent
        return mapSteps((step) =>
          activeEnumKeys.has(step.log_enum_key) ? "active" : "inactive",
        );
      }
    },
    [timelineStepsProp, jobStatus, placeholderValues],
  );

  /**
   * Memoized timeline steps: Only recalculate when timelineData actually changes
   * Prevents creating new array references unnecessarily
   */
  const memoizedTimelineSteps = useMemo(
    () => mapApiResponseToTimelineSteps(timelineData),
    [timelineData, mapApiResponseToTimelineSteps],
  );

  /**
   * Update timeline steps when memoized steps change
   * This prevents unnecessary state updates and re-renders
   */
  React.useEffect(() => {
    setTimelineSteps(memoizedTimelineSteps);
  }, [memoizedTimelineSteps]);
  /**
   * Check if the last timeline step is completed
   * This is used to determine if the "Go to Dashboard" button should be enabled
   */
  React.useEffect(() => {
    if (
      timelineData &&
      timelineData.length > 0 &&
      timelineStepsProp.length > 0
    ) {
      const lastStep = timelineStepsProp[timelineStepsProp.length - 1];
      const lastStepEnumKey = lastStep.log_enum_key;
      const lastStepData = timelineData.find(
        (item: any) => item.log_enum_key === lastStepEnumKey,
      );
      setIsLastStepCompleted(!!lastStepData);
    } else {
      setIsLastStepCompleted(false);
    }
  }, [timelineData, timelineStepsProp]);

  /**
   * Easing function for smooth scroll animation
   * Uses ease-in-out cubic for professional, polished feel
   * t: current time (0 to 1)
   * Returns: eased progress (0 to 1)
   */
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  /**
   * Custom smooth scroll animation with easing
   * - Duration: 600ms for smooth, professional feel
   * - Easing: ease-in-out cubic for gentle acceleration/deceleration
   * - Uses requestAnimationFrame for smooth 60 FPS animation
   */
  const smoothScrollToElement = useCallback(
    (element: Element, container: Element, duration: number = 600) => {
      const startScrollTop = container.scrollTop;
      const targetScrollTop =
        element.getBoundingClientRect().top +
        container.scrollTop -
        container.getBoundingClientRect().top;

      // Calculate the distance to scroll
      const distance = targetScrollTop - startScrollTop;

      // If already in view, don't scroll
      if (Math.abs(distance) < 10) {
        return;
      }

      const startTime = performance.now();

      const scroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        container.scrollTop = startScrollTop + distance * easedProgress;

        if (progress < 1) {
          requestAnimationFrame(scroll);
        }
      };

      requestAnimationFrame(scroll);
    },
    [],
  );

  /**
   * Auto-scroll effect: Scroll to the highest active step when timeline updates
   * - Triggered when highestMatchedPosition changes
   * - Finds the timeline item corresponding to the highest matched position
   * - Uses custom smooth scroll with easing for polished animation
   * - Duration: 600ms with ease-in-out cubic easing
   */
  React.useEffect(() => {
    if (highestMatchedPosition > 0 && dialogContentRef.current) {
      // Use setTimeout to ensure DOM has been updated
      setTimeout(() => {
        const timelineContainer = dialogContentRef.current?.querySelector(
          ".timeline-container",
        );
        if (!timelineContainer) return;

        // Find the timeline item for the highest matched ID
        const timelineItems =
          timelineContainer.querySelectorAll(".timeline-item");
        const targetItem = timelineItems[highestMatchedPosition - 1]; // Position is 1-based, array is 0-based

        if (targetItem && dialogContentRef.current) {
          // Use custom smooth scroll with easing
          smoothScrollToElement(targetItem, dialogContentRef.current, 600);
        }
      }, 0);
    }
  }, [highestMatchedPosition, smoothScrollToElement]);
  /**
   * Fetch job status from API
   * - For validation jobs: calls getResourceResultByResourceId
   * - For comparison jobs: calls getRuleResultByRuleId
   * - Extracts job_status from response
   */
  const fetchJobStatus = useCallback(async () => {
    if (!executionId || executionId <= 0) return;

    try {
      let response;
      if (jobType === "validation") {
        response = await getValidationExecutionJobStatus(executionId);
      } else if (jobType === "comparison") {
        response = await getRuleExecutionJobStatus(executionId);
      }

      if (response && response.job_status) {
        setJobStatus(response.job_status);

        // Show toast notification for failure states
        if (
          response.job_status === "failed" ||
          response.job_status === "aborted"
        ) {
          showToast(
            `The execution ${executionId} is already ${response.job_status}`,
            "error",
          );
        } else if (response.job_status === "completed_with_errors") {
          showToast(
            `The execution ${executionId} is ${response.job_status}`,
            "warning",
          );
        } else if (response.job_status === "completed") {
          showToast(
            `The execution ${executionId} is ${response.job_status}`,
            "success",
          );
        }
      }
    } catch (err) {
      console.error("Failed to fetch job status:", err);
    }
  }, [executionId, jobType, showToast]);

  /**
   * Job status polling effect
   * - Polls job status at regular intervals
   * - Starts when dialog opens
   * - Stops when dialog closes or component unmounts
   * - Stops when job reaches terminal state (completed, failed, aborted, completed_with_errors)
   */
  React.useEffect(() => {
    if (!open || !executionId || executionId <= 0) {
      // Clear interval if dialog is closed
      if (jobStatusPollingIntervalId) {
        clearInterval(jobStatusPollingIntervalId);
        setJobStatusPollingIntervalId(null);
      }
      return;
    }

    // Fetch job status immediately on dialog open
    fetchJobStatus();

    // Set up polling interval
    const intervalId = setInterval(() => {
      fetchJobStatus();
    }, intervalMs);

    setJobStatusPollingIntervalId(intervalId);

    // Cleanup: Clear interval when dialog closes or component unmounts
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, executionId]);

  // Generate dynamic title if not provided
  const dialogTitle =
    title || `${jobType.charAt(0).toUpperCase() + jobType.slice(1)} Timeline`;

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    } else {
      onClose();
    }
  };
  /**
   * Handle "Go to Dashboard" button click
   * - Navigates to the appropriate dashboard based on jobType
   * - Only enabled when job_status is "completed" and last step is completed
   */
  const handleGoToDashboard = () => {
    if (
      (jobStatus === "completed" || jobStatus === "completed_with_errors") &&
      isLastStepCompleted &&
      executionId
    ) {
      if (jobType === "validation") {
        navigate(
          `/validation-execution-history/validate-result/${executionId}`,
        );
      } else if (jobType === "comparison") {
        navigate(
          `/rules-execution-history/${executionId}/${executionId}/dashboard`,
        );
      }
      onClose();
    }
  };

  const handleRefresh = async () => {
    try {
      // Fetch the timeline data
      const data = await getTimelineStatus(
        executionId as number,
        jobType as "validation" | "comparison" | "generic_research_query",
      );
      applyTimelineData(data);
      if (isMountedRef.current) {
        setError(null);
      }
      // Also fetch job status to update button state immediately
      await fetchJobStatus();
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(
        err instanceof Error ? err.message : "Failed to fetch timeline data",
      );
    }
  };

  /**
   * Handle manual refresh button click
   * Resets the auto-refresh timer to start counting from 0 again
   * Does not restart recurring polling once the terminal step has been reached
   */
  const handleManualRefresh = async () => {
    // Clear existing auto-refresh interval
    if (autoRefreshIntervalId) {
      clearInterval(autoRefreshIntervalId);
      setAutoRefreshIntervalId(null);
    }

    // Perform the refresh
    await handleRefresh();

    // Restart the auto-refresh interval
    if (
      open &&
      executionId &&
      executionId > 0 &&
      !isTerminalStepReachedRef.current
    ) {
      const newIntervalId = setInterval(() => {
        handleRefresh();
      }, intervalMs);

      setAutoRefreshIntervalId(newIntervalId);
    }
  };

  const handleDialogClose = (
    _event: {},
    _reason: "backdropClick" | "escapeKeyDown",
  ) => {
    // Allow closing on backdrop click and ESC key for this dialog
    onClose();
  };

  /**
   * Extract resource parameter rows from the fetched execution result data.
   * - Validation: flat top-level object → single row
   * - Comparison: { resources: [...] } array → one row per resource
   */
  const getParamRows = (): any[] => {
    if (!validationParams) return [];

    if (jobType === "validation") {
      // Validation: single resource object nested in validation_request_body
      const ValidationResourceData =
        validationParams?.complete_validation_params?.validation_request_body
          ?.resource_data || {};
      const completeValidationParams: any =
        validationParams?.complete_validation_params || {};

      return [
        {
          id: 0,
          resource_name: ValidationResourceData.resource_name ?? null,
          resource_id: ValidationResourceData.resource_id ?? null,
          file_name: ValidationResourceData.file_name ?? null,
          file_names: completeValidationParams.file_names ?? null,
          connection_type_code:
            ValidationResourceData.linked_service_code ?? null,
          connection_key: ValidationResourceData.connection_key ?? null,
          resource_location: ValidationResourceData.resource_path ?? null,
          additional_resource_info: completeValidationParams,
        },
      ];
    }

    if (jobType === "comparison") {
      // Comparison: resource_data is a list
      const ComparisonResourceList: any[] =
        validationParams?.complete_execution_params?.rule_execution_request
          ?.request_body?.resource_data || [];
      const completeExecutionParams: any =
        validationParams?.complete_execution_params || {};

      if (ComparisonResourceList.length > 0) {
        return ComparisonResourceList.map((res: any, idx: number) => ({
          id: idx,
          resource_name: res?.resource_name ?? null,
          resource_id: res?.resource_id ?? null,
          file_name: res?.file_name ?? null,
          connection_type_code: res?.linked_service_code ?? null,
          connection_key: res?.connection_key ?? null,
          resource_location: res?.resource_path ?? null,
          additional_resource_info: completeExecutionParams,
        }));
      }

      // Fallback: when resource_data is absent, use additional_info_for_all_resources
      const additionalInfoList: any[] =
        validationParams?.complete_execution_params?.rule_execution_request
          ?.additional_info_for_all_resources || [];
      if (additionalInfoList.length > 0) {
        return additionalInfoList.map((res: any, idx: number) => ({
          id: idx,
          resource_name: null,
          resource_id: res?.resource_id ?? null,
          file_name:
            res?.file_names?.length > 0 ? res.file_names.join(", ") : null,
          connection_type_code: null,
          connection_key: null,
          resource_location: null,
          additional_resource_info: completeExecutionParams,
        }));
      }
    }

    return [];
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      className={`timeline-dialog ${className}`}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className="dialog-title">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <span className="dialog-title-text">{dialogTitle}</span>
          <Box display="flex" alignItems="center" gap={1}>
            {activeTab === 0 && (
              <Tooltip title="Refresh timeline" placement="top" arrow>
                <button
                  className="close-button"
                  onClick={handleManualRefresh}
                  aria-label="Refresh timeline"
                >
                  <RefreshIcon />
                </button>
              </Tooltip>
            )}
            {activeTab === 1 && (
              <Tooltip title="Refresh params" placement="top" arrow>
                <button
                  className="close-button"
                  onClick={handleRefreshParams}
                  aria-label="Refresh params"
                >
                  <RefreshIcon />
                </button>
              </Tooltip>
            )}

            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <CloseIcon />
            </button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className="dialog-content" ref={dialogContentRef}>
        {/* Tab navigation */}
        <Tabs
          value={activeTab}
          onChange={(_e, newVal) => setActiveTab(newVal)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
        >
          <Tab label="Timeline" />
          <Tab
            label={
              jobType === "comparison"
                ? "Rule Parameters"
                : "Validation Parameters"
            }
          />
        </Tabs>

        {/* Tab 0: Timeline */}
        {activeTab === 0 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {timelineSteps.length > 0 && (
              <VerticalTimeline steps={timelineSteps} />
            )}
          </>
        )}

        {/* Tab 1: Validation Parameters */}
        {activeTab === 1 && (
          <Box sx={{ mt: 1 }}>
            {isParamsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : getParamRows().length > 0 ? (
              getParamRows().map((row, idx) => (
                <Box key={row.id}>
                  {idx > 0 && (
                    <Box
                      sx={{
                        my: 1.5,
                        px: 1,
                        py: 0.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        fontWeight: "bold",
                        fontSize: "0.8rem",
                      }}
                    ></Box>
                  )}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {[
                          {
                            label: "Resource Name (Resource ID)",
                            value: row.resource_name
                              ? `${row.resource_name}${row.resource_id ? ` (${row.resource_id})` : ""}`
                              : row.resource_id
                                ? `(${row.resource_id})`
                                : "—",
                          },
                          { label: "File Name", value: row.file_name || "—" },
                          {
                            label: "Connection Type Code",
                            value: row.connection_type_code || "—",
                          },
                          {
                            label: "Connection Key",
                            value: String(row.connection_key ?? "—"),
                          },
                          {
                            label: "Resource Location",
                            value: row.resource_location || "—",
                          },
                        ].map(({ label, value }) => (
                          <TableRow key={label}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                width: "40%",
                                color: "text.secondary",
                                fontSize: "0.8rem",
                              }}
                            >
                              {label}
                            </TableCell>
                            <TableCell
                              sx={{
                                wordBreak: "break-all",
                                fontSize: "0.85rem",
                              }}
                            >
                              {value}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Additional Resource Info — info icon opens JSON popover */}
                        {row.additional_resource_info && (
                          <TableRow key="additional-resource-info">
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                width: "40%",
                                color: "text.secondary",
                                fontSize: "0.8rem",
                              }}
                            >
                              Additional Resource Info
                            </TableCell>
                            <TableCell>
                              <span className="position-relative">
                                <Tooltip
                                  componentsProps={{
                                    tooltip: {
                                      className: "wide-tooltip w-380",
                                    },
                                  }}
                                  title={
                                    <pre
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        margin: 0,
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                      }}
                                    >
                                      <React.Fragment>
                                        <Typography color="inherit">
                                          Additional Resource Info
                                        </Typography>
                                        <Typography>
                                          {formattedJson(
                                            JSON.stringify(
                                              row.additional_resource_info,
                                            ),
                                          )}
                                        </Typography>
                                      </React.Fragment>
                                    </pre>
                                  }
                                >
                                  <InfoIcon
                                    sx={{ width: "16px", cursor: "pointer" }}
                                  />
                                </Tooltip>
                              </span>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                Data loading is currently in progress. Please try after some
                time.
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {showActions && (
        <DialogActions className="dialog-actions">
          <Box className="action-buttons" sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleGoToDashboard}
              disabled={
                !(
                  (jobStatus === "completed" ||
                    jobStatus === "completed_with_errors") &&
                  isLastStepCompleted
                )
              }
              className="btn-orange"
              variant="contained"
              color="primary"
              title={
                jobStatus !== "completed" &&
                jobStatus !== "completed_with_errors"
                  ? "Job is still in progress"
                  : !isLastStepCompleted
                    ? "Waiting for last step to complete"
                    : "Go to Dashboard"
              }
            >
              Go to Dashboard
            </Button>
            <Button
              onClick={handleSecondaryAction}
              className="btn-orange btn-dark"
              variant="contained"
              color="secondary"
            >
              {secondaryButtonText}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DynamicVerticalTimelineDialog;
